/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useCallback, useEffect, useState } from "react";
import {
  getDatabase,
  onValue,
  ref,
  remove,
  set,
  update,
} from "firebase/database";
import { useAuth } from "@/contexts/AuthContext";
import type { CircleMember, VerificationRecord } from "@/types/safecircle";

export function useCircleMembers(profileId: string | null | undefined) {
  const { user } = useAuth();
  const [members, setMembers] = useState<CircleMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !profileId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    const db = getDatabase();
    const membersRef = ref(db, `safecircle/${user.uid}/circles/${profileId}`);
    const unsub = onValue(membersRef, (snapshot) => {
      const data = snapshot.val();
      if (data && typeof data === "object") {
        const list = Object.entries(data).map(
          ([id, val]) => ({ ...(val as CircleMember), memberId: id }),
        );
        setMembers(list);
      } else {
        setMembers([]);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [user, profileId]);

  const addMember = useCallback(
    (member: Omit<CircleMember, "addedAt">) => {
      if (!user || !profileId) return Promise.resolve();
      const db = getDatabase();
      const record: CircleMember = { ...member, addedAt: Date.now() };
      // Firebase RTDB rejects undefined values anywhere in the payload. Strip
      // them (and any nested undefined inside optional sub-objects) by round-
      // tripping through JSON with a replacer that swaps undefined for null.
      const sanitized = JSON.parse(
        JSON.stringify(record, (_k, v) => (v === undefined ? null : v)),
      );
      return set(
        ref(
          db,
          `safecircle/${user.uid}/circles/${profileId}/${member.memberId}`,
        ),
        sanitized,
      );
    },
    [user, profileId],
  );

  const updateMember = useCallback(
    (memberId: string, updates: Partial<CircleMember>) => {
      if (!user || !profileId) return;
      const db = getDatabase();
      update(
        ref(db, `safecircle/${user.uid}/circles/${profileId}/${memberId}`),
        updates,
      );
    },
    [user, profileId],
  );

  const updateVerification = useCallback(
    (memberId: string, record: VerificationRecord) => {
      if (!user || !profileId) return;
      const db = getDatabase();
      const memberRef = ref(
        db,
        `safecircle/${user.uid}/circles/${profileId}/${memberId}`,
      );
      update(memberRef, {
        verification: record,
        verified:
          record.level === "mutually_verified" ||
          record.level === "community_vouched",
      });
    },
    [user, profileId],
  );

  const removeMember = useCallback(
    (memberId: string) => {
      if (!user || !profileId) return;
      const db = getDatabase();
      remove(
        ref(db, `safecircle/${user.uid}/circles/${profileId}/${memberId}`),
      );
    },
    [user, profileId],
  );

  return { members, loading, addMember, updateMember, updateVerification, removeMember };
}
